/**
 * Finance / Budget KPI scoring engine.
 *
 * Mirrors kpi-engine/schedule.ts but operates on cumulative cost data
 * (budget baseline vs actual spend) instead of schedule % complete.
 *
 * Key differences from schedule.ts:
 *  - Thresholds are percentage-based (% over budget) instead of percentage-point based.
 *  - CPI = baseline_budget / actual_spend (EVM convention: CPI > 1 = under budget).
 *  - "Behind" in cost terms means spending MORE than planned (over-budget).
 */

import type { KpiSignal } from "./types";
import { latestAsOf, previousBeforeAsOf } from "./timeSeries";
import { KPI_DEFAULTS } from "./defaults";

// ── Types ────────────────────────────────────────────────────

export type CostPoint = {
  project_GUID: string;
  reporting_date: string;
  baseline_budget: number; // cumulative planned spend
  actual_spend: number; // cumulative actual spend
  forecast_spend?: number;
};

export type CostToleranceConfig = {
  /** Actual spend exceeds baseline by >= this % → yellow. Default 5. */
  yellow_threshold_pct: number;
  /** Actual spend exceeds baseline by >= this % → red. Default 10. */
  red_threshold_pct: number;
  /** At or beyond this % over budget → score 0. Default 40. */
  score_zero_at_over_pct?: number;
  /** Epsilon for trend flatness. Default 0.25. */
  trend_epsilon_pct?: number;
};

export type CpiHistoryPoint = {
  date: string;
  cpi: number;
  baseline_budget: number;
  actual_spend: number;
  /** True when baseline < early_maturity_guard_pct — CPI is mathematically
   *  valid but unreliable due to near-zero denominator. */
  belowGuard?: boolean;
};

export type CpiToleranceConfig = {
  /** CPI >= this is green. Default 0.95. */
  warning_threshold: number;
  /** CPI < this is red. Default 0.90. */
  critical_threshold: number;
  /** Suppress CPI when baseline spend < this % of total budget. Default 5. */
  early_maturity_guard_pct: number;
};

export const CPI_TOLERANCE_DEFAULTS: CpiToleranceConfig = {
  warning_threshold: KPI_DEFAULTS.cpi.warning_threshold,
  critical_threshold: KPI_DEFAULTS.cpi.critical_threshold,
  early_maturity_guard_pct: KPI_DEFAULTS.cpi.early_maturity_guard_pct,
};

// ── Internal helpers ─────────────────────────────────────────

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/**
 * Compute "over-budget percentage".
 * overPct = ((actual - baseline) / baseline) * 100
 * Positive means over-budget; negative means under-budget.
 */
function computeOverBudgetPct(baseline: number, actual: number): number {
  if (baseline <= 0) return 0;
  return ((actual - baseline) / baseline) * 100;
}

function indicatorColorFromOverPct(
  overPct: number,
  cfg: CostToleranceConfig,
): number {
  if (!Number.isFinite(overPct)) return 0;
  // 1=red, 2=yellow, 3=green
  if (overPct >= cfg.red_threshold_pct) return 1;
  if (overPct >= cfg.yellow_threshold_pct) return 2;
  return 3;
}

function scoreFromOverPct(
  overPct: number,
  cfg: CostToleranceConfig,
): number {
  const zeroAt = cfg.score_zero_at_over_pct ?? KPI_DEFAULTS.budget.score_zero_at_over_pct;
  if (overPct <= 0) return 100; // under or on budget
  const s = 100 - (overPct * 100) / zeroAt;
  return Math.round(clamp(s, 0, 100));
}

// ── Main cost variance signal ────────────────────────────────

/**
 * Compute the Budget/Cost KPI signal from cost data points as-of a date.
 * Uses baseline budget vs actual spend.
 */
export function computeCostKpiSignal(
  points: CostPoint[],
  asOfDate: string,
  cfg: CostToleranceConfig,
): KpiSignal {
  const pts = points
    .filter((p) => {
      const act = (p as any).actual_spend ?? (p as any).actual;
      return act !== null && act !== undefined;
    })
    .map((p) => ({
      date: (p as any).reporting_date ?? (p as any).date,
      baseline: (p as any).baseline_budget ?? (p as any).baseline,
      actual: (p as any).actual_spend ?? (p as any).actual,
    }));

  const cur = latestAsOf(pts, asOfDate);
  if (!cur) {
    return {
      score_0_100: null,
      indicator_color_value: 0,
      indicator_trend_value: 0,
      as_of_date: asOfDate,
      explain: {
        headline: "No budget/cost data available as of this date.",
      },
    };
  }

  const prev = previousBeforeAsOf(pts, asOfDate);
  const overPct = computeOverBudgetPct(cur.baseline ?? 0, cur.actual ?? 0);
  const score = scoreFromOverPct(overPct, cfg);
  const colorValue = indicatorColorFromOverPct(overPct, cfg);

  const eps = cfg.trend_epsilon_pct ?? KPI_DEFAULTS.budget.trend_epsilon_pct;
  let trend: -1 | 0 | 1 = 0;
  if (prev) {
    const prevOver = computeOverBudgetPct(
      prev.baseline ?? 0,
      prev.actual ?? 0,
    );
    // Improving = overPct going down (spending less relative to budget)
    if (overPct < prevOver - eps) trend = 1;
    else if (overPct > prevOver + eps) trend = -1;
  }

  return {
    score_0_100: score,
    indicator_color_value: colorValue,
    indicator_trend_value: trend,
    as_of_date: cur.date,
    explain: {
      headline:
        overPct <= 0
          ? "On or under budget."
          : `Over budget by ${overPct.toFixed(1)}% as of ${cur.date}.`,
      details: {
        baseline_budget: cur.baseline,
        actual_spend: cur.actual,
        over_budget_pct: Number(overPct.toFixed(3)),
        previous_date: prev?.date ?? null,
      },
    },
  };
}

// ── CPI (Cost Performance Index) — EVM-lite ──────────────────

/**
 * Compute CPI = baseline_budget / actual_spend at each reporting date.
 * CPI > 1 means under budget; CPI < 1 means over budget.
 *
 * Early maturity guardrail: points where baseline spend is too small
 * relative to total are excluded to prevent noise.
 */
export function computeCpiHistorySeries(
  points: CostPoint[],
  config?: Partial<CpiToleranceConfig>,
): CpiHistoryPoint[] {
  const cfg: CpiToleranceConfig = { ...CPI_TOLERANCE_DEFAULTS, ...config };
  if (!Array.isArray(points) || points.length === 0) return [];

  const sorted = [...points].sort((a, b) =>
    String(a.reporting_date).localeCompare(String(b.reporting_date)),
  );

  const out: CpiHistoryPoint[] = [];
  for (const p of sorted) {
    const baseline = Number(p.baseline_budget);
    const actual = Number(p.actual_spend);
    if (!Number.isFinite(baseline) || !Number.isFinite(actual)) continue;

    // Skip truly-zero baselines (CPI is undefined when planned = 0).
    if (baseline <= 0 && actual <= 0) continue;

    // CPI = planned / actual (cost convention)
    // CPI > 1 = under budget (good), CPI < 1 = over budget (bad)
    const cpi = actual > 0 ? baseline / actual : baseline > 0 ? 2 : 0;
    if (!Number.isFinite(cpi)) continue;

    out.push({
      date: p.reporting_date,
      cpi,
      baseline_budget: baseline,
      actual_spend: actual,
      // Early maturity flag — chart renders these with lower visual weight.
      belowGuard: baseline < cfg.early_maturity_guard_pct,
    });
  }
  return out;
}

/**
 * Returns the most recent CPI point at or before `asOfDate`.
 *
 * Points flagged `belowGuard` are skipped — they're included in the
 * history for chart display but should not drive the gauge or score.
 */
export function getCurrentCpiAsOf(
  history: CpiHistoryPoint[],
  asOfDate: string,
): CpiHistoryPoint | null {
  if (!history.length) return null;
  let best: CpiHistoryPoint | null = null;
  for (const p of history) {
    if (p.belowGuard) continue;
    if (p.date <= asOfDate && (best === null || p.date > best.date)) {
      best = p;
    }
  }
  return best;
}

/**
 * Returns the CPI color band code for a given CPI value.
 * 0 = no data, 1 = red (critical), 2 = yellow (warning), 3 = green (on track).
 */
export function getCpiColorBand(
  cpi: number,
  config?: Partial<CpiToleranceConfig>,
): 0 | 1 | 2 | 3 {
  const cfg: CpiToleranceConfig = { ...CPI_TOLERANCE_DEFAULTS, ...config };
  if (!Number.isFinite(cpi)) return 0;
  if (cpi < cfg.critical_threshold) return 1;
  if (cpi < cfg.warning_threshold) return 2;
  return 3;
}
